// "Remove area" (DD, 2026-10-15): needs signal, asks first, and is offered only
// once the area and its photos have synced — so nothing removed is the
// measurer's only copy. CRM retires the area (it can be restored there); its
// photos stay on SharePoint.
import { ApiError, isConnectionFailure } from '../lib/api';
import type { DevicePhoto, DeviceRecord } from '../lib/device-db';
import { useOnline } from '../lib/hooks';
import { ServerRemoveButton } from './remove-button';

/** Why the area can't be removed from this device right now; null when it can. */
function blockedBecause(area: DeviceRecord, photos: readonly DevicePhoto[], online: boolean): string | null {
  if (!online) return 'Removing an area needs signal.';
  if (area.state !== 'synced') return 'This area has changes still to sync. Let them sync, then remove it.';
  if (photos.some((p) => p.state === 'waiting')) return 'This area has photos still to upload. Let them upload, then remove it.';
  return null;
}

function describe(err: unknown): string {
  if (isConnectionFailure(err)) return 'Removing an area needs signal.';
  if (err instanceof ApiError) return err.message;
  return 'The area could not be removed.';
}

interface Props {
  area: DeviceRecord;
  photos: readonly DevicePhoto[];
  /** Removes it (CRM, then this device); the caller moves off the area. */
  onRemove: () => Promise<void>;
  /**
   * Why a removal was refused (null to clear). The caller shows it on its own
   * line under the toolbar, so a long message never moves the buttons (#399).
   */
  onError: (message: string | null) => void;
}

export function RemoveArea({ area, photos, onRemove, onError }: Props) {
  const online = useOnline();
  const name = String(area.fields.hig_name ?? 'this area');
  return (
    <ServerRemoveButton
      label="Remove area"
      blocked={blockedBecause(area, photos, online)}
      question={
        <>
          Remove <strong>{name}</strong> from this job? Its surfaces and site details go from the measure and the price. Its photos stay on
          SharePoint.
        </>
      }
      onRemove={onRemove}
      onError={onError}
      describe={describe}
    />
  );
}
